import React from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import { updateDocumentByID } from 'apis/customApiHandler';
import Modal from 'components/Modal';
import SelectField from 'components/formik/SelectField';
import InputField from 'components/formik/InputField';
import { Button } from 'styled';
import toast from 'react-hot-toast';

const Edit = ({ data, handleClick }) => {
  const queryClient = useQueryClient();

  const initialValues = {
    tyreSize: data?.tyreSize || '',
    skuName: data?.skuName || '',
    materialDescription: data?.materialDescription || '',
    originalNsd: data?.originalNsd ?? '',
    status: data?.active === false ? 'DISABLE' : 'ENABLE', 
  };

  const Schema = Yup.object({
    status: Yup.mixed().oneOf(['ENABLE', 'DISABLE']).required('Required'),
  });

  const { mutateAsync, isLoading: saving } = useMutation((payload) =>
    updateDocumentByID('material-master', data?._id, payload)
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Schema,
    onSubmit: async (values) => {
      const payload = {
        active: values.status === 'ENABLE',
      };

      try {
        const resp = await mutateAsync(payload);
        toast.success(resp?.message || 'Material updated');
        queryClient.invalidateQueries('material-master');
        handleClick?.();
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Something went wrong';
        toast.error(msg);
      }
    },
  });

  return (
    <Modal
      handleClick={handleClick}
      title="Update Material Master"
      className="max-w-2xl min-h-fit"
    >
      <FormikProvider value={formik}>
        <Form className="px-5 py-5 text-gray-700 font-medium text-base grid lg:gap-x-14 gap-y-5 grid-flow-row grid-cols-1 lg:grid-cols-2 w-full">

          <InputField
            formik={formik}
            name="tyreSize"
            label="Tyre Size"
            required={false}
            desable={true}
          />

          <InputField
            formik={formik}
            name="skuName"
            label="SKU Name"
            type="text"
            required={false}
            placeholder="e.g. 10.00R20 16PR PRO B12"
            desable={true}
          />

          <InputField
            formik={formik}
            name="materialDescription"
            label="Material Description"
            type="text"
            required={false}
            desable={true}
          />

          <InputField
            formik={formik}
            name="originalNsd"
            label="Original NSD"
            type="number"
            required={false}
            desable={true}
          />

          <div className="flex flex-col justify-end">
            <label className="mb-2">Status</label>
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="ENABLE"
                  checked={formik.values.status === 'ENABLE'}
                  onChange={() => formik.setFieldValue('status', 'ENABLE')}
                />
                <span>Enable</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="DISABLE"
                  checked={formik.values.status === 'DISABLE'}
                  onChange={() => formik.setFieldValue('status', 'DISABLE')}
                />
                <span>Disable</span>
              </label>
            </div>
            {formik.touched.status && formik.errors.status ? (
              <div className="mt-1 text-sm text-red-600">
                {formik.errors.status}
              </div>
            ) : null}
          </div>

          <div />

          <div className="flex items-end justify-end gap-4 mt-6 col-span-1 lg:col-span-2">
            <Button type="button" onClick={handleClick} bgSecondary>
              <span>Cancel</span>
            </Button>
            <Button type="submit" disabled={saving}>
              <span>{saving ? 'Saving…' : 'Save'}</span>
            </Button>
          </div>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default Edit;
